import { useState, useEffect, useRef } from "react";
import Vapi from "@vapi-ai/web";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Phone, PhoneOff, Mic, MicOff, Volume2 } from "lucide-react";

// Hardcoded credentials for testing
const VAPI_PUBLIC_KEY = " ";
const ASSISTANT_ID = "851ce70e-d925-4eb5-a545-1228f21762b4";

interface VapiMessage {
  type: string;
  role?: string;
  transcript?: string;
  transcriptType?: string;
  functionCall?: {
    name: string;
    parameters: unknown;
  };
}

export default function TestVoicePage() {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [transcript, setTranscript] = useState<string[]>([]);
  const [status, setStatus] = useState<string>("Ready to call");
  const vapiRef = useRef<Vapi | null>(null);

  useEffect(() => {
    // Initialize Vapi instance
    const vapi = new Vapi(VAPI_PUBLIC_KEY);
    vapiRef.current = vapi;

    // Event listeners
    vapi.on("speech-start", () => {
      setStatus("Assistant is speaking...");
    });

    vapi.on("speech-end", () => {
      setStatus("Listening...");
    });

    vapi.on("call-start", () => {
      console.log("📞 Call started");
      setIsCallActive(true);
      setIsConnecting(false);
      setStatus("Call started - speak to the assistant");
    });

    vapi.on("call-end", () => {
      console.log("📞 Call ended");
      setIsCallActive(false);
      setIsConnecting(false);
      setIsMuted(false);
      setVolumeLevel(0);
      setStatus("Call ended");
    });

    vapi.on("volume-level", (volume: number) => {
      setVolumeLevel(volume);
    });

    vapi.on("message", (message: VapiMessage) => {
      console.log("Vapi message:", message);

      // Handle transcript messages
      if (message.type === "transcript" && message.transcript) {
        const prefix = message.role === "user" ? "You: " : "Assistant: ";
        const transcriptType =
          message.transcriptType === "final" ? "" : " (partial)";

        setTranscript((prev) => {
          // If it's a partial transcript, update the last message of the same role
          if (message.transcriptType === "partial") {
            const lastIndex = prev.length - 1;
            if (
              lastIndex >= 0 &&
              prev[lastIndex].startsWith(prefix.replace(": ", ""))
            ) {
              const newTranscript = [...prev];
              newTranscript[lastIndex] =
                `${prefix}${message.transcript}${transcriptType}`;
              return newTranscript;
            }
          }
          return [...prev, `${prefix}${message.transcript}${transcriptType}`];
        });
      }

      // Handle function calls
      if (message.type === "function-call" && message.functionCall) {
        setTranscript((prev) => [
          ...prev,
          `[Function Call: ${message.functionCall?.name}]`,
        ]);
      }
    });

    vapi.on("error", (error: Error) => {
      console.error("Vapi error:", error);
      setStatus(`Error: ${error.message || "Unknown error"}`);
      setIsCallActive(false);
      setIsConnecting(false);
    });

    return () => {
      // Cleanup
      vapi.stop();
    };
  }, []);

  const startCall = async () => {
    if (!vapiRef.current) return;

    try {
      setIsConnecting(true);
      setStatus("Connecting...");
      setTranscript([]);

      await vapiRef.current.start(ASSISTANT_ID);
    } catch (error) {
      console.error("Error starting call:", error);
      setStatus(
        `Failed to start call: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
      setIsConnecting(false);
    }
  };

  const endCall = () => {
    if (!vapiRef.current) return;
    vapiRef.current.stop();
  };

  const toggleMute = () => {
    if (!vapiRef.current) return;
    const newMuteState = !isMuted;
    vapiRef.current.setMuted(newMuteState);
    setIsMuted(newMuteState);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg border-0 shadow-2xl bg-slate-800/80 backdrop-blur-xl text-white">
        <CardHeader className="text-center border-b border-slate-700/50 pb-6">
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Voice Call Test
          </CardTitle>
          <CardDescription className="text-slate-400">
            Test the Vapi voice assistant integration
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6 pt-6">
          {/* Status Badge */}
          <div className="flex items-center justify-center gap-3">
            <Badge
              variant={
                isCallActive
                  ? "default"
                  : isConnecting
                    ? "secondary"
                    : "outline"
              }
              className={`
                text-sm px-4 py-1.5 
                ${isCallActive ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}
                ${isConnecting ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30 animate-pulse" : ""}
              `}
            >
              {status}
            </Badge>
          </div>

          {/* Volume Level Indicator */}
          {isCallActive && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-700/30 border border-slate-600/30">
              <Volume2 className="w-5 h-5 text-purple-400" />
              <div className="flex-1 h-2 bg-slate-600/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-100"
                  style={{ width: `${Math.min(volumeLevel * 100, 100)}%` }}
                />
              </div>
              <span className="text-xs text-slate-400 w-12">
                {Math.round(volumeLevel * 100)}%
              </span>
            </div>
          )}

          {/* Transcript Area */}
          <div className="relative">
            <ScrollArea className="h-64 w-full rounded-xl border border-slate-600/30 bg-slate-900/50 p-4">
              {transcript.length === 0 ? (
                <p className="text-slate-500 text-center italic">
                  Call transcript will appear here...
                </p>
              ) : (
                <div className="space-y-2">
                  {transcript.map((msg, index) => (
                    <p
                      key={index}
                      className={`text-sm ${
                        msg.startsWith("You:")
                          ? "text-blue-400"
                          : msg.startsWith("Assistant:")
                            ? "text-purple-300"
                            : "text-yellow-400"
                      }`}
                    >
                      {msg}
                    </p>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </CardContent>

        <CardFooter className="flex justify-center gap-4 pt-4 pb-6 border-t border-slate-700/50">
          {!isCallActive && !isConnecting ? (
            <Button
              onClick={startCall}
              size="lg"
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg shadow-green-500/25 transition-all duration-200"
            >
              <Phone className="w-5 h-5 mr-2" />
              Start Call
            </Button>
          ) : (
            <>
              <Button
                onClick={toggleMute}
                size="lg"
                variant="outline"
                className={`
                  border-slate-600 hover:bg-slate-700/50 transition-all duration-200
                  ${isMuted ? "bg-red-500/20 border-red-500/50 text-red-400" : ""}
                `}
              >
                {isMuted ? (
                  <MicOff className="w-5 h-5 mr-2" />
                ) : (
                  <Mic className="w-5 h-5 mr-2" />
                )}
                {isMuted ? "Unmute" : "Mute"}
              </Button>
              <Button
                onClick={endCall}
                size="lg"
                className="bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-lg shadow-red-500/25 transition-all duration-200"
                disabled={isConnecting}
              >
                <PhoneOff className="w-5 h-5 mr-2" />
                End Call
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
